import { api } from '@/shared/redux/api/_generated/api/openplc-api.generated';

// Events and mutations carry partial views, so re-reading the status is the only safe refresh.
const projectStatusTag = (projectId: string) => [
  { type: 'OpenPlcProjectStatus' as const, id: projectId },
];

const ORCHESTRATOR_LIST = { type: 'Orchestrator' as const, id: 'LIST' };

// vPLC mutations carry only a vplcId, so the list tag is their only reach — and only one
// vPLC list is ever mounted, so it over-invalidates nothing.
const VPLC_LIST = { type: 'Vplc' as const, id: 'LIST' };

export const openplcApi = api.enhanceEndpoints({
  addTagTypes: [
    'OpenPlcProjectStatus',
    'Orchestrator',
    'Vplc',
    'VplcCreateOptions',
    'OrchestratorInstallCommand',
  ],
  endpoints: {
    ReadOpenPlcProjectStatus: {
      providesTags: (_result, _error, { projectId }) =>
        projectStatusTag(projectId),
    },
    OpenEditSession: {
      invalidatesTags: (_result, _error, { projectId }) =>
        projectStatusTag(projectId),
    },
    EndEditSession: {
      invalidatesTags: (_result, _error, { projectId }) =>
        projectStatusTag(projectId),
    },
    AcceptStagedChange: {
      invalidatesTags: (_result, _error, { projectId }) =>
        projectStatusTag(projectId),
    },
    DiscardStagedChange: {
      invalidatesTags: (_result, _error, { projectId }) =>
        projectStatusTag(projectId),
    },
    ReadOrchestrators: {
      providesTags: () => [ORCHESTRATOR_LIST],
    },
    ReadVplcs: {
      providesTags: (_result, _error, { orchestratorId }) => [
        VPLC_LIST,
        { type: 'Vplc' as const, id: orchestratorId },
      ],
    },
    // Nothing invalidates it, so holding it forever costs one request per session.
    ReadOrchestratorInstallCommand: {
      providesTags: () => [
        {
          type: 'OrchestratorInstallCommand' as const,
          id: 'SINGLETON',
        },
      ],
      keepUnusedDataFor: Infinity,
    },
    ReadVplcCreateOptions: {
      providesTags: (_result, _error, { orchestratorId }) => [
        { type: 'VplcCreateOptions' as const, id: orchestratorId },
      ],
    },
    RegisterOrchestrator: {
      invalidatesTags: () => [ORCHESTRATOR_LIST],
    },
    RenameOrchestrator: {
      invalidatesTags: () => [ORCHESTRATOR_LIST],
    },
    // Restarting the host restarts the containers on it.
    OrchestratorCommand: {
      invalidatesTags: () => [ORCHESTRATOR_LIST, VPLC_LIST],
    },
    DeleteOrchestrator: {
      invalidatesTags: () => [ORCHESTRATOR_LIST, VPLC_LIST],
    },
    CreateVplc: {
      invalidatesTags: (_result, _error, { input }) => [
        VPLC_LIST,
        { type: 'VplcCreateOptions' as const, id: input.orchestratorId },
      ],
    },
    RenameVplc: {
      invalidatesTags: () => [VPLC_LIST],
    },
    VplcCommand: {
      invalidatesTags: () => [VPLC_LIST],
    },
    DeleteVplc: {
      invalidatesTags: () => [VPLC_LIST],
    },
  },
});

export const {
  useReadOpenPlcProjectStatusQuery,
  useOpenEditSessionMutation,
  useEndEditSessionMutation,
  useAcceptStagedChangeMutation,
  useDiscardStagedChangeMutation,
  useReadOrchestratorsQuery,
  useReadVplcsQuery,
  useReadVplcCreateOptionsQuery,
  useReadOrchestratorInstallCommandQuery,
  useRegisterOrchestratorMutation,
  useRenameOrchestratorMutation,
  useOrchestratorCommandMutation,
  useDeleteOrchestratorMutation,
  useCreateVplcMutation,
  useRenameVplcMutation,
  useVplcCommandMutation,
  useDeleteVplcMutation,
} = openplcApi;
